/**
 * Travel stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const travelStickersPack: ToilePack = {
    slug: 'travel-stickers',
    category: 'sticker',
    name: 'Travel stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'label', label: 'Luggage label', subject: 'a vintage luggage label with a blank centre band and a string'},
        {key: 'compass', label: 'Compass', subject: 'a round compass with a red needle'},
        {key: 'plane', label: 'Paper plane', subject: 'a folded paper plane with a dotted trail'},
    ],
    styles: [
        {key: 'retro', label: 'Retro', style: 'retro sticker illustration, 1970s shapes, halftone grain, slight misregistration', palette: 'Mustard, rust, teal and cream', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'bold', label: 'Bold cartoon', style: 'bold cartoon sticker, thick black outlines, bright flat fills, slight gloss', palette: 'Bright saturated primaries with black outlines', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'minimal', label: 'Minimal line', style: 'minimal single-line sticker, one clean continuous line, no fill', palette: 'Black line only, one accent colour', framing: 'Each sticker isolated with a thin white die-cut border on a plain ground'},
    ],
    directions: ['a passport stamp instead', 'for a suitcase', 'in vintage sepia tones'],
    directionHint: 'try: a passport stamp instead',
    seo: {
        title: 'Travel Stickers: Luggage Label, Compass, Plane',
        metaDescription: 'Nine travel stickers — a vintage luggage label, a compass, a paper plane — in retro, bold cartoon, and minimal line styles.',
        h1: 'Travel stickers: luggage label, compass, paper plane',
        tagline: 'Nine stickers, three travel icons, three styles each.',
        intro: 'A vintage luggage label with a blank centre band, a round compass with a red needle, and a folded paper plane with a dotted trail — three travel stickers, die-cut and ready to print. Each rendered retro, bold cartoon, and minimal single-line. Ask for a specific city, a passport-stamp look, or a suitcase composition, and the same three styles carry it.',
        useCases: [
            'a suitcase or laptop lid',
            'a travel journal or planner',
            'a water bottle or tote bag',
            'a going-away gift',
        ],
        faq: [
            {q: "What's in this pack?", a: 'Nine travel stickers — a vintage luggage label, a compass, and a paper plane — in retro, bold cartoon, and minimal line finishes.'},
            {q: 'Can I get a specific city or destination?', a: 'Yes — the direction field takes any variation, including a specific city, a passport-stamp look, or a suitcase composition.'},
            {q: 'What size should I print these at?', a: 'Each sticker is a square die-cut design, so it scales cleanly from a small vinyl sticker to a full suitcase-sized sheet.'},
            {q: 'Does the luggage label carry real text?', a: 'No — the label ships with a blank centre band by design, so it prints clean or takes your own lettering afterward.'},
        ],
    },
}
